import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AddMemberBody,
  type BatchMembersBody,
  type MemberListParams,
  adminClient,
} from '../lib/admin-client.js';

export const MEMBER_KEYS = {
  all: (tenantId: string, roleId: string) => ['admin-role-members', tenantId, roleId] as const,
  list: (tenantId: string, roleId: string, params: MemberListParams) =>
    ['admin-role-members', tenantId, roleId, 'list', params] as const,
};

export function useRoleMembers(tenantId: string, roleId: string, params: MemberListParams = {}) {
  return useQuery({
    queryKey: MEMBER_KEYS.list(tenantId, roleId, params),
    queryFn: () => adminClient.listRoleMembers(tenantId, roleId, params),
    enabled: tenantId.length > 0 && roleId.length > 0,
    staleTime: 15_000,
  });
}

export function useAddRoleMember(tenantId: string, roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddMemberBody) => adminClient.addRoleMember(tenantId, roleId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MEMBER_KEYS.all(tenantId, roleId) });
    },
  });
}

export function useRemoveRoleMember(tenantId: string, roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => adminClient.removeRoleMember(tenantId, roleId, memberId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MEMBER_KEYS.all(tenantId, roleId) });
    },
  });
}

export function useBatchRoleMembers(tenantId: string, roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchMembersBody) => adminClient.batchRoleMembers(tenantId, roleId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MEMBER_KEYS.all(tenantId, roleId) });
    },
  });
}
